import { apiClient as api } from "./httpClient";

// Áreas de IA da página Assistentes e as decisões sobre as propostas dos agentes (RN-565/566/567).

export interface MyAssistantArea {
  areaKey: string;
  readAgentId: string;
  readAgentName: string;
  /** Nulo = a pessoa só tem consulta nesta área. */
  writeAgentName?: string | null;
  /** Resposta falada ligada nesta área (RN-570); só vale com a voz de pé no ambiente. */
  speechReply: boolean;
}

export type ProposalStatus = "pending" | "confirmed" | "discarded" | "rejected" | "expired" | "undone";

export interface ProposalCardData {
  id: string;
  action:
    | "schedule_insert" | "schedule_reschedule" | "schedule_inactivate" | "schedule_replace"
    // Livro-caixa (RN-575): cartão genérico com titulo/alcance/itens no preview
    | "cashbook_insert" | "cashbook_settle_provision" | "cashbook_settle" | "cashbook_edit" | "cashbook_inactivate"
    | "memory_save";
  status: ProposalStatus;
  /** "De → para" em linguagem da clínica (JSON montado pelo servidor). */
  previewJson: string;
  resultMessage?: string | null;
}

export interface ProposalDecision {
  proposalId: string;
  status: ProposalStatus;
  resultTargetId?: string | null;
  undoProposalId?: string | null;
}

/** Extrato lido e confrontado com o Livro-caixa (RN-571). Valores com sinal: entrada positiva, saída negativa. */
export type StatementProof = "CLOSED" | "TOTALS" | "BROKEN" | "NONE";

export interface StatementReviewLine {
  row: number;
  date: string;
  description: string;
  amount: number;
  status: "in_book" | "new";
  bookDate?: string | null;
}

export interface StatementReview {
  accountTitle: string;
  periodStart?: string | null;
  periodEnd?: string | null;
  openingBalance?: number | null;
  closingBalance?: number | null;
  proof: StatementProof;
  brokenAtRow?: number | null;
  credits: number;
  debits: number;
  inBookCount: number;
  newCount: number;
  lines: StatementReviewLine[];
  onlyInBook: { date: string; description: string; amount: number }[];
  warnings: string[];
}

/** RN-574: needsAccount = não reconhecida, a pessoa escolhe (sem nada marcado); recognizedBy = "numero" | "memoria". */
export interface StatementReviewResult {
  needsAccount: boolean;
  accountId?: string | null;
  recognizedBy?: string | null;
  fingerprint: string;
  review?: StatementReview | null;
}

export interface StatementAccount {
  id: string;
  title: string;
  description?: string | null;
}

/** Regra gravada pela equipe (RN-577): `when`/`then` em linguagem da clínica; só a regra em texto é editável. */
export interface AiMemoryItem {
  id: string;
  kind: string;
  kindLabel: string;
  when: string;
  then: string;
  editable: boolean;
  updatedAt: string;
}

export const assistantsService = {
  memories: async (): Promise<AiMemoryItem[]> => (await api.get("/assistants/memories")).data,
  updateMemoryText: async (id: string, text: string): Promise<void> => { await api.put(`/assistants/memories/${id}`, { text }); },
  forgetMemory: async (id: string): Promise<void> => { await api.delete(`/assistants/memories/${id}`); },
  myAreas: async (): Promise<MyAssistantArea[]> => (await api.get("/assistants/areas")).data,
  statementAccounts: async (): Promise<StatementAccount[]> => (await api.get("/assistants/cash-book/statements/accounts")).data,
  // Content-Type nulo: o apiClient manda JSON por padrão e isso vaza para o FormData (415).
  // Sem conta: o servidor reconhece pelo extrato (RN-574) ou devolve needsAccount para a pessoa escolher.
  // remember: só com a pessoa marcando "Lembrar esta conta" a escolha vira memória da clínica (RN-575)
  reviewStatement: async (file: File, entityAccountId?: string, remember = false): Promise<StatementReviewResult> => {
    const form = new FormData();
    form.append("file", file, file.name);
    if (entityAccountId) form.append("entityAccountId", entityAccountId);
    if (entityAccountId && remember) form.append("remember", "true");
    return (await api.post("/assistants/cash-book/statements/review", form, { headers: { "Content-Type": null } })).data;
  },
  confirm: async (id: string): Promise<ProposalDecision> => (await api.post(`/assistants/proposals/${id}/confirm`)).data,
  discard: async (id: string): Promise<ProposalDecision> => (await api.post(`/assistants/proposals/${id}/discard`)).data,
  undo: async (id: string): Promise<ProposalDecision> => (await api.post(`/assistants/proposals/${id}/undo`)).data,
};
